// @flow

import {
  DISMISS_ERROR,
  LOAD_PATIENT,
  LOAD_PATIENTS,
  LOAD_PATIENT_DONE,
  LOG_OUT,
  PATIENTS_LOADED,
  PATIENTS_RESET_LOADING,
  PATIENT_ADD,
  PATIENT_ADD_DONE,
  PATIENT_ADD_ERROR,
  PATIENT_UPDATE,
  PATIENT_UPDATE_DONE,
  SET_PATIENTS_FILTER,
  PATIENTS_RESET_TO_MODE,
  SET_ACTIVE_PATIENT,
  ADD_TO_FILTER_FROM_DASHBOARD,
  REFRESH_PATIENTS,
  REFRESH_PATIENTS_AND_RESET_PAGE,
  SAVED_SEARCHES_LOADED,
  LOG_IN_SUCCESS,
  SET_EDIT_PATIENT_CP,
  UPDATE_PATIENT_IN_LIST,
  REMOVE_PATIENT_FROM_LIST,
  ADD_PATIENT_TO_LIST,
  NAVIGATE_TO_ALL_PATIENTS,
  NAVIGATE_TO_WATCHLIST,
  NAVIGATE_TO_MY_PATIENTS,
  NAVIGATE_TO_SAVED_QUERY,
} from "../actions/actionTypes";
import { REHYDRATE } from "redux-persist";
import { findWithAttr } from "../../utils/utils";
import { devLog } from "../../utils/devLog";

// const PATIENTS_ALL_FILTER = "PATIENTS_ALL_FILTER";
// const PATIENTS_WATCHLIST_FILTER = "PATIENTS_WATCHLIST_FILTER";
// const PATIENTS__DASHBOARD_FILTER = "PATIENTS__DASHBOARD_FILTER";

// Initial state for individual list types
const initialListState = {
  list: [],
  loading: false,
  page: 0,
  loadMore: true,
  total: undefined,
  activePatientId: null,
  error: false,
  addSuccess: false,
  inviteSuccess: false,
};

const initialState = {
  // Separate list states by type
  all: { ...initialListState },
  watchlist: { ...initialListState },
  dashboard: { ...initialListState },
  savedQuery: {}, // Object to store list states by queryId

  // Shared state for filters
  allFilter: null,
  watchlistFilter: null,
  dashboardFilter: null,
  savedQueryFilters: {}, // Object to store filters by queryId

  // Legacy shared state properties (for backward compatibility)
  mode: "all",
  fresh: true,
  list: [], // Will be populated from current list type
  loading: false,
  page: 0,
  loadMore: true,
  error: false,
  addSuccess: false,
  inviteSuccess: false,
  activePatientId: null,
  savedSearches: [],
};

// Helper function to get list type from criteria/mode
function getListTypeFromCriteria(criteria) {
  //devLog("[getListTypeFromCriteria] input:", criteria);
  if (!criteria) return "all";

  // Check for queryId first - this indicates a saved query
  if (criteria.queryId) {
    return "savedQuery";
  }

  // Check for dashboard navigation - if we have statField or stat parameters, it's from dashboard
  if (criteria.statField || criteria.stat) {
    return "dashboard";
  }

  // Check if we're coming from dashboard route
  if (
    typeof window !== "undefined" &&
    window.location &&
    window.location.pathname.includes("/patients-list/dashboard")
  ) {
    return "dashboard";
  }

  // Check the mode property
  if (criteria.mode === "GETPATIENTS_TAGGED") {
    //devLog("[getListTypeFromCriteria] returning watchlist");
    return "watchlist";
  } else if (criteria.mode === "GETPATIENTS_ALL") {
    //devLog("[getListTypeFromCriteria] returning all");
    return "all";
  } else if (criteria.mode === "GETPATIENTS_DASHBOARD") {
    return "dashboard";
  }

  // Fallback to 'all'
  //devLog("[getListTypeFromCriteria] fallback to all");
  return "all";
}

// Helper function to get the current list state
function getCurrentListState(state, listType, queryId = null) {
  if (listType === "savedQuery" && queryId) {
    // Safety check: ensure savedQuery object exists before accessing
    return (state.savedQuery && state.savedQuery[queryId]) || { ...initialListState };
  }
  return state[listType] || { ...initialListState };
}

// Helper function to update list state and sync legacy properties
function updateListState(state, listType, updates, queryId = null) {
  let newState = { ...state };

  if (listType === "savedQuery" && queryId) {
    newState.savedQuery = {
      ...state.savedQuery,
      [queryId]: {
        ...getCurrentListState(state, listType, queryId),
        ...updates,
      },
    };
  } else {
    newState[listType] = {
      ...state[listType],
      ...updates,
    };
  }

  // Always sync to legacy properties for backward compatibility
  const currentListState = getCurrentListState(newState, listType, queryId);
  newState.list = currentListState.list || [];
  newState.loading = currentListState.loading || false;
  newState.page = currentListState.page || 0;
  newState.loadMore = currentListState.loadMore || false;
  newState.total = currentListState.total;
  newState.activePatientId = currentListState.activePatientId;
  newState.error = currentListState.error || false;
  newState.addSuccess = currentListState.addSuccess || false;
  newState.inviteSuccess = currentListState.inviteSuccess || false;

  return newState;
}

const patientsReducer = (state = initialState, action) => {
  let list, index;
  switch (action.type) {
    case PATIENTS_RESET_LOADING:
      //devLog("[patientsReducer] PATIENTS_RESET_LOADING received:", action);
      const resetListType = getListTypeFromCriteria(action.criteria);
      const resetQueryId = action.criteria?.queryId;

      return updateListState(
        state,
        resetListType,
        {
          list: [],
          loading: false,
          page: 0,
          loadMore: true,
          total: undefined,
          error: false,
          addSuccess: false,
          inviteSuccess: false,
        },
        resetQueryId
      );

    case PATIENT_UPDATE:
    case PATIENT_ADD:
    case LOAD_PATIENT:
    case LOAD_PATIENTS:
      //devLog("[patientsReducer] LOAD_PATIENTS action:", action);
      const loadingListType = getListTypeFromCriteria(
        action.criteria || action.searchCriteria
      );
      const loadingQueryId = (action.criteria || action.searchCriteria)
        ?.queryId;

      //devLog(
      //  "[patientsReducer] LOAD_PATIENTS - setting loading=true for:",
      //  loadingListType
      //);

      return updateListState(
        state,
        loadingListType,
        {
          loading: true,
        },
        loadingQueryId
      );

    case DISMISS_ERROR:
      return { ...state, error: false, errorMsg: "" };

    case PATIENT_ADD_ERROR:
      return { ...state, loading: false, error: true, errorMsg: action.error };

    case LOAD_PATIENT_DONE:
    case PATIENT_UPDATE_DONE:
    case PATIENT_ADD_DONE:
      list = state.list.slice(0);

      const patientId = action.data.patientId || -1;
      index = findWithAttr(list, "patientId", patientId);

      // not found then return prev state
      if (index === -1)
        return {
          ...state,
          loading: false,
          list: [action.data].concat(list),
          addPatientId: patientId,
          addSuccess:
            action.type === PATIENT_ADD_DONE ||
            action.type === PATIENT_UPDATE_DONE,
        };

      // if found, replace whole object
      list[index] = action.data;

      return {
        ...state,
        loading: false,
        list: list,
        addPatientId: patientId,
        addSuccess:
          action.type === PATIENT_ADD_DONE ||
          action.type === PATIENT_UPDATE_DONE,
      };

    case PATIENTS_LOADED:
      const loadedListType = getListTypeFromCriteria(action.criteria);
      const loadedQueryId = action.criteria?.queryId;
      const currentState = getCurrentListState(
        state,
        loadedListType,
        loadedQueryId
      );

      // The saga sends page: currentPage + 1, so page === 1 means this was the first load (page 0)
      const isFirstLoad = action.page === 1;

      //devLog("[patientsReducer] PATIENTS_LOADED - setting loading=false for:", {
      //  listType: loadedListType,
      //  actionPage: action.page,
      //  isFirstLoad,
      //  currentListLength: currentState.list?.length || 0,
      //  newDataLength: action.list?.length || 0,
      //  willReplace: isFirstLoad,
      //  willAppend: !isFirstLoad,
      //});

      return updateListState(
        state,
        loadedListType,
        {
          loading: false,
          page: action.page,
          loadMore: action.loadMore,
          total: action.total,
          list: isFirstLoad
            ? action.list
            : (currentState.list || []).concat(action.list),
        },
        loadedQueryId
      );

    case LOG_IN_SUCCESS:
      return {
        ...state,
        fresh: true,
      };

    case SET_PATIENTS_FILTER:
      let mode = action.mode;
      //devLog("[patientsReducer] SET_PATIENTS_FILTER:", {
      //  mode,
      //  searchTerm: action.filter.searchTerm,
      //});
      let newState = {
        ...state,
        fresh: false,
      };
      if (mode === "all") {
        // REMOVED: action.filter.searchTerm = ""; // This was clearing the searchTerm!
        // storageService.addToLocalStorage(
        //   PATIENTS_ALL_FILTER + "_" + action.staffId,
        //   action.filter
        // );
        newState.allFilter = action.filter;
        //devLog(
      //    "[patientsReducer] SET_PATIENTS_FILTER - saved allFilter with searchTerm:",
      //    newState.allFilter.searchTerm
      //);
      } else if (mode === "watchlist") {
        // storageService.addToLocalStorage(
        //   PATIENTS_WATCHLIST_FILTER,
        //   action.filter
        // );
        newState.watchlistFilter = action.filter;
      } else if (
        mode === "saved-query" &&
        action.filter &&
        action.filter.queryId
      ) {
        // Store the filter for the specific saved query to survive page refreshes and browser navigation
        newState.savedQueryFilters = {
          ...state.savedQueryFilters,
          [action.filter.queryId]: action.filter,
        };
      }
      return newState;

    case ADD_TO_FILTER_FROM_DASHBOARD:
      let filter =
        state.dashboardFilter || getDashboardFilterFromAllFilter(state) || {};
      if (
        filter.uiFieldsWithoutOperators &&
        filter.uiFieldsWithoutOperators.length >= 1
      ) {
        return getStateAfterAddingToFilter(
          state,
          action,
          "uiFieldsWithoutOperators"
        );
      } else if (
        filter.uiFieldsWithOperators &&
        filter.uiFieldsWithOperators.length >= 1
      ) {
        return getStateAfterAddingToFilter(
          state,
          action,
          "uiFieldsWithOperators"
        );
      } else {
        return { ...state };
      }

    case REFRESH_PATIENTS:
      // Set fresh to true but preserve all existing filters including savedQueryFilters
      return {
        ...state,
        fresh: true,
      };

    case REFRESH_PATIENTS_AND_RESET_PAGE:
      // Set fresh to true and reset page to 0 for all list types (used after successful patient add)
      return {
        ...state,
        fresh: true,
        page: 0,
        all: { ...state.all, page: 0 },
        watchlist: { ...state.watchlist, page: 0 },
        dashboard: { ...state.dashboard, page: 0 },
        savedQuery: Object.keys(state.savedQuery).reduce((acc, queryId) => {
          acc[queryId] = { ...state.savedQuery[queryId], page: 0 };
          return acc;
        }, {}),
      };

    case PATIENTS_RESET_TO_MODE:
      let rMode = action.mode;
      let dNewState = {
        ...initialListState,
        savedSearches: state.savedSearches,
        mode: rMode,
      };

      if (rMode === "all") {
        dNewState.allFilter = null;
      } else if (rMode === "watchlist") {
        dNewState.watchlistFilter = null;
      } else if (rMode === "dashboard") {
        dNewState.dashboardFilter = null;
      }
      return dNewState;

    case SET_ACTIVE_PATIENT:
      const activeListType = getListTypeFromCriteria(action.criteria);
      const activeQueryId = action.criteria?.queryId;

      return updateListState(
        state,
        activeListType,
        {
          activePatientId: action.patientId,
        },
        activeQueryId
      );

    case LOG_OUT:
      return initialState;

    case REHYDRATE:
      //devLog(
      //  "[patientsReducer] REHYDRATE action:",
      //  action.payload?.patients?.allFilter?.searchTerm
      //);
      if (action.payload && action.payload.patients) {
        const restoredState = { ...action.payload.patients };

        // DEBUG: Log all filter states being restored
        //devLog("[patientsReducer] REHYDRATE - Restoring filter states:", {
        //  allFilter: {
        //    exists: !!restoredState.allFilter,
        //    searchTerm: restoredState.allFilter?.searchTerm,
        //    hasUiFieldsWithOperators: !!restoredState.allFilter?.uiFieldsWithOperators,
        //    uiFieldsWithOperatorsCount: restoredState.allFilter?.uiFieldsWithOperators?.length || 0,
        //    hasUiFieldsWithoutOperators: !!restoredState.allFilter?.uiFieldsWithoutOperators,
        //    uiFieldsWithoutOperatorsCount: restoredState.allFilter?.uiFieldsWithoutOperators?.length || 0,
        //    hasSortField: !!restoredState.allFilter?.sortField,
        //    sortFieldAttribute: restoredState.allFilter?.sortFieldAttribute,
        //    sortDirection: restoredState.allFilter?.sortDirection,
        //  },
        //  watchlistFilter: {
        //    exists: !!restoredState.watchlistFilter,
        //    searchTerm: restoredState.watchlistFilter?.searchTerm,
        //    hasUiFields: !!restoredState.watchlistFilter?.uiFieldsWithOperators || !!restoredState.watchlistFilter?.uiFieldsWithoutOperators,
        //  },
        //  dashboardFilter: {
        //    exists: !!restoredState.dashboardFilter,
        //    searchTerm: restoredState.dashboardFilter?.searchTerm,
        //    hasUiFields: !!restoredState.dashboardFilter?.uiFieldsWithOperators || !!restoredState.dashboardFilter?.uiFieldsWithoutOperators,
        //  },
        //  savedQueryFilters: Object.keys(restoredState.savedQueryFilters || {}).length > 0
        //    ? Object.keys(restoredState.savedQueryFilters).map(queryId => ({
        //        queryId,
        //        hasUiFields: !!(restoredState.savedQueryFilters[queryId]?.uiFields),
        //        uiFieldsCount: restoredState.savedQueryFilters[queryId]?.uiFields?.length || 0,
        //      }))
        //    : "none"
        //});

        // Check if any filters have searchTerm - if not, clear the corresponding list results
        // This prevents showing stale search results when searchTerm was cleared from filter
        if (restoredState.allFilter && !restoredState.allFilter.searchTerm) {
          //devLog(
          //  "[patientsReducer] REHYDRATE clearing all list - no searchTerm in allFilter"
          //);
          // Clear the 'all' list since there's no searchTerm but we might have search results
          restoredState.all = { ...initialListState };
          restoredState.list = [];
          restoredState.loading = false;
          restoredState.page = 0;
          restoredState.loadMore = true;
          restoredState.total = undefined;
        } else {
          //devLog(
          //  "[patientsReducer] REHYDRATE preserving all list - searchTerm exists:",
          //  restoredState.allFilter?.searchTerm
          //);
        }

        // Only clear watchlist if we specifically had search results that are now stale
        // Don't clear normal watchlist data just because there's no searchTerm
        if (
          restoredState.watchlistFilter &&
          restoredState.watchlistFilter.searchTerm === "" &&
          restoredState.watchlist?.list?.length > 0
        ) {
          //devLog(
          //  "[patientsReducer] REHYDRATE clearing watchlist - had stale search results"
          //);
          restoredState.watchlist = { ...initialListState };
        }

        if (
          restoredState.dashboardFilter &&
          !restoredState.dashboardFilter.searchTerm
        ) {
          restoredState.dashboard = { ...initialListState };
        }

        return {
          ...state,
          ...restoredState,
          loading: false,
        };
      }
      return state;

    case SAVED_SEARCHES_LOADED:
      return {
        ...state,
        savedSearches: action.savedSearches,
      };

    case SET_EDIT_PATIENT_CP:
      return {
        ...state,
        patientCPData: action.data,
      };

    case UPDATE_PATIENT_IN_LIST:
      const updatePatientId = action.patientId;
      //devLog("[patientsReducer] UPDATE_PATIENT_IN_LIST:", {
      //  patientId: updatePatientId,
      //  updates: action.updates,
      //  availableListTypes: Object.keys(state).filter((k) =>
      //    ["all", "watchlist", "dashboard"].includes(k)
      //  ),
      //});
      let updatedState = { ...state };

      // Update patient in shared/legacy list
      list = state.list.slice(0);
      index = findWithAttr(list, "patientId", updatePatientId);
      if (index !== -1) {
        list[index] = { ...list[index], ...action.updates };
        updatedState.list = list;
      }

      // Also update patient in all separated list states (all, watchlist, dashboard)
      ["all", "watchlist", "dashboard"].forEach((listType) => {
        if (state[listType] && state[listType].list) {
          const listTypeList = state[listType].list.slice(0);
          const listTypeIndex = findWithAttr(
            listTypeList,
            "patientId",
            updatePatientId
          );
          if (listTypeIndex !== -1) {
            listTypeList[listTypeIndex] = {
              ...listTypeList[listTypeIndex],
              ...action.updates,
            };
            updatedState[listType] = { ...state[listType], list: listTypeList };
          }
        }
      });

      // Update saved query lists as well
      if (state.savedQuery) {
        const updatedSavedQuery = { ...state.savedQuery };
        Object.keys(updatedSavedQuery).forEach((queryId) => {
          const queryList = updatedSavedQuery[queryId]?.list;
          if (queryList) {
            const queryListCopy = queryList.slice(0);
            const queryIndex = findWithAttr(
              queryListCopy,
              "patientId",
              updatePatientId
            );
            if (queryIndex !== -1) {
              queryListCopy[queryIndex] = {
                ...queryListCopy[queryIndex],
                ...action.updates,
              };
              updatedSavedQuery[queryId] = {
                ...updatedSavedQuery[queryId],
                list: queryListCopy,
              };
            }
          }
        });
        updatedState.savedQuery = updatedSavedQuery;
      }

      return updatedState;

    case REMOVE_PATIENT_FROM_LIST:
      const removePatientId = action.patientId;
      //devLog("[patientsReducer] REMOVE_PATIENT_FROM_LIST:", {
      //  patientId: removePatientId,
      //  availableListTypes: Object.keys(state).filter((k) =>
      //    ["all", "watchlist", "dashboard"].includes(k)
      //  ),
      //});

      let removedState = { ...state };

      // Remove patient from shared/legacy list
      list = state.list.slice(0);
      const removeIndex = findWithAttr(list, "patientId", removePatientId);
      if (removeIndex !== -1) {
        list.splice(removeIndex, 1);
        removedState.list = list;
        removedState.total = state.total ? state.total - 1 : 0;
      }

      // Also remove patient from all separated list states (all, watchlist, dashboard)
      ["all", "watchlist", "dashboard"].forEach((listType) => {
        //devLog(`[patientsReducer] Checking ${listType} for patient removal:`, {
        //  hasListType: !!state[listType],
        //  hasList: !!(state[listType] && state[listType].list),
        //  listLength: state[listType]?.list?.length || 0,
        //});

        if (state[listType] && state[listType].list) {
          const listTypeList = state[listType].list.slice(0);
          const listTypeIndex = findWithAttr(
            listTypeList,
            "patientId",
            removePatientId
          );
          //devLog(
          //  `[patientsReducer] Patient ${removePatientId} found at index ${listTypeIndex} in ${listType}`
          //);

          if (listTypeIndex !== -1) {
            //devLog(
            //  `[patientsReducer] Removing patient from ${listType} - before: ${
            //    listTypeList.length
            //  }, after: ${listTypeList.length - 1}`
            //);
            listTypeList.splice(listTypeIndex, 1);
            const newTotal = state[listType].total
              ? state[listType].total - 1
              : 0;
            removedState[listType] = {
              ...state[listType],
              list: listTypeList,
              total: newTotal,
            };
            //devLog(`[patientsReducer] Updated ${listType} state:`, {
            //  listLength: removedState[listType].list.length,
            //  total: removedState[listType].total,
            //});
          }
        }
      });

      // Remove from saved query lists as well
      if (state.savedQuery) {
        const removedSavedQuery = { ...state.savedQuery };
        Object.keys(removedSavedQuery).forEach((queryId) => {
          const queryList = removedSavedQuery[queryId]?.list;
          if (queryList) {
            const queryListCopy = queryList.slice(0);
            const queryIndex = findWithAttr(
              queryListCopy,
              "patientId",
              removePatientId
            );
            if (queryIndex !== -1) {
              queryListCopy.splice(queryIndex, 1);
              const newTotal = removedSavedQuery[queryId].total
                ? removedSavedQuery[queryId].total - 1
                : 0;
              removedSavedQuery[queryId] = {
                ...removedSavedQuery[queryId],
                list: queryListCopy,
                total: newTotal,
              };
            }
          }
        });
        removedState.savedQuery = removedSavedQuery;
      }

      return removedState;

    case ADD_PATIENT_TO_LIST:
      list = state.list.slice(0);
      const newPatient = action.patient;

      // Check if patient already exists to avoid duplicates
      const existingIndex = findWithAttr(
        list,
        "patientId",
        newPatient.patientId
      );
      if (existingIndex !== -1) {
        // Patient already exists, just update it
        list[existingIndex] = newPatient;
      } else {
        // Add new patient to the beginning of the list
        list.unshift(newPatient);
      }

      return {
        ...state,
        list: list,
        total: existingIndex === -1 ? (state.total || 0) + 1 : state.total, // Update total count only if new
      };

    case NAVIGATE_TO_ALL_PATIENTS:
      //devLog(
      //  "[patientsReducer] NAVIGATE_TO_ALL_PATIENTS - resetting all list and setting mode"
      //);
      return updateListState(state, "all", {
        ...initialListState,
        fresh: true,
      });

    case NAVIGATE_TO_WATCHLIST:
      //devLog(
      //  "[patientsReducer] NAVIGATE_TO_WATCHLIST - resetting watchlist and setting mode"
      //);
      return updateListState(state, "watchlist", {
        ...initialListState,
        fresh: true,
      });

    case NAVIGATE_TO_MY_PATIENTS:
      //devLog(
      //  "[patientsReducer] NAVIGATE_TO_MY_PATIENTS - resetting all list and setting mode"
      //);
      // My Patients uses the same list type as 'all' but with different filter criteria
      return updateListState(state, "all", {
        ...initialListState,
        fresh: true,
      });

    case NAVIGATE_TO_SAVED_QUERY:
      //devLog(
      //  "[patientsReducer] NAVIGATE_TO_SAVED_QUERY - resetting saved query and setting mode",
      //  action.queryId
      //);

      // Always reset state for navigation actions to avoid timing issues
      // This ensures clean state and prevents multiple simultaneous API calls
      //devLog(
      //  "[patientsReducer] NAVIGATE_TO_SAVED_QUERY - always resetting to clean state for navigation"
      //);

      return updateListState(
        state,
        "savedQuery",
        {
          ...initialListState,
          fresh: true,
        },
        action.queryId
      );

    default:
      return state;
  }
};

function getStateAfterAddingToFilter(state, action, uiFieldsName) {
  let filter =
    state.dashboardFilter || getDashboardFilterFromAllFilter(state) || {};
  let uiFields = filter[uiFieldsName];
  uiFields.forEach((uiField) => {
    uiField.value = null;
  });
  action.fields.forEach((aField) => {
    let index = uiFields.findIndex((field) => field.name === aField.fieldName);

    if (index !== -1) {
      let filterField = uiFields[index];
      filterField.value = {
        valueObservation: {
          value: {
            valueString: aField.fieldValue,
          },
          code: {
            code: filterField.observationCode.code,
          },
        },
      };
      uiFields[index] = {
        ...filterField,
      };
      filter[uiFieldsName] = [...uiFields];
    }
  });

  // storageService.addToLocalStorage(PATIENTS__DASHBOARD_FILTER, filter);
  return {
    ...state,
    dashboardFilter: {
      ...filter,
    },
    fresh: true,
  };
}

function getDashboardFilterFromAllFilter(state) {
  let allFilter = state.allFilter || {};
  let uiFieldsWithoutOperators = allFilter.uiFieldsWithoutOperators || [];
  let uiFieldsWithOperators = allFilter.uiFieldsWithOperators || [];

  if (uiFieldsWithoutOperators.length >= 1) {
    uiFieldsWithoutOperators.forEach((field) => {
      delete field.value;
    });
  }

  if (uiFieldsWithOperators.length >= 1) {
    uiFieldsWithOperators.forEach((field) => {
      delete field.value;
    });
  }

  return allFilter;
}

export default patientsReducer;
